import { GPUParticleSystem, ParticleSystem, Vector3 } from '@babylonjs/core';
import { Button, Control, TextBlock } from '@babylonjs/gui';
import { Game } from './Game';
import { GameConfig } from './GameConfig';
import { GameState } from './GameState';
import { Gui, MenuNavigation } from './gui/Gui';
import { LevelResult } from './Level';
import { LevelConfigLoader } from './LevelConfigLoader';
import { Particles } from './Particles';

interface BonusItem {
  name: string;
  value: number;
  condition: boolean;
}

export class LevelSuccess {
  private scoreValueBlock: TextBlock;
  private bonusNameBlock: TextBlock;
  private currentDisplayedScore: number = 0;
  private activeParticleSystems: (ParticleSystem | GPUParticleSystem)[] = [];
  private nav: MenuNavigation;

  constructor(
    public game: Game,
    public levelResult: LevelResult
  ) {
    if (!this.levelResult) {
      // happens when called from editor
      this.levelResult = {
        key: 'test',
        name: 'Test Level',
        collectibles: 20,
        enemies: 20,
        totalCollectibles: 20,
        totalEnemies: 20,
        remainingTime: 30,
        score: 1000,
        coins: 73,
        lives: 4,
        attempts: 2,
      };
    }
  }

  async create() {
    await this.game.sound.create('bonus1', 'match_synth_1', 'fx');
    await this.game.sound.create('bonus2', 'match_synth_2', 'fx');
    await this.game.sound.create('bonus3', 'match_synth_3', 'fx');
    await this.game.sound.create('bonus4', 'match_synth_4', 'fx');
    await this.game.sound.create('bonus5', 'match_synth_5', 'fx');
    await this.game.sound.create('bonus6', 'match_synth_6', 'fx');
    await this.game.sound.create('bonus7', 'match_synth_7', 'fx');
    await this.game.sound.create('bonus8', 'match_synth_8', 'fx');
    await this.game.sound.create('bonus9', 'match_synth_9', 'fx');
    await this.game.sound.create('bonus10', 'match_synth_10_MAX', 'fx');

    await Gui.createDefaultScene(
      this.game,
      {
        url: 'assets/skybox/skybox_autumn_forest.glb',
        rotationAxis: 'y',
        rotation: new Vector3(-Math.PI / 2, 0, 0),
        rotationSpeed: 0.0001,
      },
      'Level Completed!'
    );

    await this.game.sound.createBackgroundMusic('Oniku');

    const title = Gui.createTitle(this.levelResult.name);
    title.top = '-8%';
    this.game.gui2D.ui.addControl(title);

    // Create player animation
    await Gui.createAnimatedPlayer(this.game, 'SambaDancing', new Vector3(-5, -1.65, 0));

    // Create visuals for stats
    this.createStatsDisplay();

    // Create bonus name overlay (initially hidden)
    this.createBonusNameOverlay();

    setTimeout(async () => {
      await this.distributeBonuses();

      // Buttons
      this.nav = new MenuNavigation(this.game);

      if (LevelConfigLoader.isLastLevel(this.levelResult.key)) {
        this.nav.addControl(await this.createFinishButton());
      } else {
        this.nav.addControl(await this.createNextLevelButton());
        this.nav.addControl(await this.createSelectLevelButton());
      }
      this.nav.start();
    }, 1000);
  }

  private createBonusNameOverlay() {
    this.bonusNameBlock = new TextBlock('bonusName');
    this.bonusNameBlock.text = '';
    this.bonusNameBlock.fontSize = 48;
    this.bonusNameBlock.fontWeight = 'bold';
    this.bonusNameBlock.fontFamily = Gui.fontFamily;
    this.bonusNameBlock.color = '#FFD700';
    this.bonusNameBlock.outlineColor = '#FF6600';
    this.bonusNameBlock.outlineWidth = 4;
    this.bonusNameBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.bonusNameBlock.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.bonusNameBlock.top = '10%';
    this.bonusNameBlock.left = '25%';
    this.bonusNameBlock.alpha = 0;
    this.bonusNameBlock.isVisible = false;
    Gui.setDefaultShadow(this.bonusNameBlock);
    this.game.gui2D.ui.addControl(this.bonusNameBlock);
  }

  private async distributeBonuses(): Promise<void> {
    // Calculate all bonuses
    const enemyPercentage = this.levelResult.totalEnemies > 0 ? this.levelResult.enemies / this.levelResult.totalEnemies : 0;
    const collectiblePercentage =
      this.levelResult.totalCollectibles > 0 ? this.levelResult.collectibles / this.levelResult.totalCollectibles : 0;

    const bonuses: BonusItem[] = [
      {
        name: '🏆 Killing Spree!',
        value: 1000,
        condition: this.levelResult.enemies === this.levelResult.totalEnemies && this.levelResult.totalEnemies > 0,
      },
      {
        name: '💎 Everything Collected!',
        value: 1000,
        condition: this.levelResult.collectibles === this.levelResult.totalCollectibles && this.levelResult.totalCollectibles > 0,
      },
      {
        name: '⭐ First Try!',
        value: 1000,
        condition: this.levelResult.attempts === 1,
      },
      {
        name: '⏱️ Time Bonus',
        value: Math.floor(this.levelResult.remainingTime * 10),
        condition: this.levelResult.remainingTime > 0,
      },
      {
        name: '👹 Enemy Bonus',
        value: Math.floor(1000 * enemyPercentage),
        condition: enemyPercentage > 0,
      },
      {
        name: '🪙 Collectible Bonus',
        value: Math.floor(1000 * collectiblePercentage),
        condition: collectiblePercentage > 0,
      },
    ];

    // Start with base score
    this.currentDisplayedScore = this.levelResult.score || 0;
    this.updateScoreDisplay();

    // Wait a moment before starting bonus distribution
    await this.delay(500);

    // Distribute each bonus sequentially
    for (const bonus of bonuses) {
      if (bonus.condition && bonus.value > 0) {
        await this.awardBonus(bonus, bonuses.indexOf(bonus));
      }
    }

    // Final celebration burst
    this.createStarBurst();
    // this.game.sound.play('bonus10');

    // Save user state after bonus distribution
    this.game.user.lives = this.levelResult.lives;
    this.game.user.coins = this.levelResult.coins;
    this.game.user.setLevelStat(LevelConfigLoader.getLevelIndex(this.levelResult.key), this.currentDisplayedScore);
    this.game.user.save();

    // Submit highscore to server
    try {
      await fetch(`${GameConfig.serverUrl}/highscores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: this.game.user.name,
          levelIndex: LevelConfigLoader.getLevelIndex(this.levelResult.key),
          score: this.currentDisplayedScore,
        }),
      });
    } catch (e) {
      console.error('Failed to submit highscore', e);
    }
  }

  private async awardBonus(bonus: BonusItem, index: number): Promise<void> {
    // Show bonus name
    await this.showBonusName(bonus.name);

    // Play sound
    this.game.sound.play(`bonus${index + 1}`);

    // Create firework effects
    this.createStarBurst();
    this.createStarBurst();
    this.createStarBurst();

    // Animate score increase
    const targetScore = this.currentDisplayedScore + bonus.value;
    await this.animateScoreIncrease(this.currentDisplayedScore, targetScore, 500);
    this.currentDisplayedScore = targetScore;

    // Keep bonus name visible briefly
    await this.delay(300);

    // Fade out bonus name
    this.hideBonusName();

    // Small pause before next bonus
    await this.delay(200);
  }

  private async showBonusName(name: string): Promise<void> {
    this.bonusNameBlock.text = name;
    this.bonusNameBlock.scaleX = 0.5;
    this.bonusNameBlock.scaleY = 0.5;
    this.bonusNameBlock.isVisible = true;

    // Animate in with scale and fade
    const duration = 200;
    const startTime = performance.now();

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out quad
        const eased = 1 - (1 - progress) * (1 - progress);

        this.bonusNameBlock.alpha = eased;
        this.bonusNameBlock.scaleX = 0.5 + 0.5 * eased;
        this.bonusNameBlock.scaleY = 0.5 + 0.5 * eased;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(animate);
    });
  }

  private hideBonusName(): void {
    const duration = 150;
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      this.bonusNameBlock.alpha = 1 - progress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.bonusNameBlock.isVisible = false;
      }
    };
    requestAnimationFrame(animate);
  }

  private async animateScoreIncrease(from: number, to: number, duration: number): Promise<void> {
    const startTime = performance.now();
    const diff = to - from;

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out
        const eased = 1 - Math.pow(1 - progress, 3);

        const currentValue = Math.floor(from + diff * eased);
        this.scoreValueBlock.text = `${currentValue}`;

        // Flash effect on score
        if (progress < 0.5) {
          this.scoreValueBlock.color = '#FFFFFF';
        } else {
          this.scoreValueBlock.color = '#FFD700';
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.scoreValueBlock.text = `${to}`;
          this.scoreValueBlock.color = '#FFD700';
          resolve();
        }
      };
      requestAnimationFrame(animate);
    });
  }

  private updateScoreDisplay(): void {
    if (this.scoreValueBlock) {
      this.scoreValueBlock.text = `${this.currentDisplayedScore}`;
    }
  }

  private createStarBurst(): void {
    // Create multiple particle bursts at random positions
    const numBursts = 3;

    for (let i = 0; i < numBursts; i++) {
      const x = (Math.random() - 0.5) * 10;
      const y = Math.random() * -8;
      const z = (Math.random() - 0.5) * 5 - 5;
      const position = new Vector3(x, y, z);

      const particleSystem = Particles.createStarParticleSystem(position, this.game);
      this.activeParticleSystems.push(particleSystem);
      particleSystem.start();

      // Dispose after animation
      setTimeout(() => {
        particleSystem.dispose();
        const idx = this.activeParticleSystems.indexOf(particleSystem);
        if (idx >= 0) {
          this.activeParticleSystems.splice(idx, 1);
        }
      }, 3000);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private createStatsDisplay() {
    const timeMs = this.levelResult.remainingTime * 1000 || 0;
    const minutes = Math.floor(timeMs / 60000);
    const seconds = Math.floor((timeMs % 60000) / 1000);

    const stats = [
      { label: 'Score', value: `${this.levelResult.score || 0}`, color: '#FFD700', ref: (b: TextBlock) => (this.scoreValueBlock = b) },
      { label: 'Coins', value: `${this.levelResult.coins || 0}`, color: '#FFD700' },
      { label: 'Remaining Time', value: `${minutes}m ${seconds}s`, color: '#00BBFF' },
      { label: 'Collectibles', value: `${this.levelResult.collectibles}/${this.levelResult.totalCollectibles}`, color: '#00BBFF' },
      { label: 'Enemies', value: `${this.levelResult.enemies}/${this.levelResult.totalEnemies}`, color: '#00BBFF' },
      { label: 'Attempts', value: `${this.levelResult.attempts}`, color: '#00BBFF' },
    ];

    const grid = this.game.gui2D.createGrid('statsGrid', '30%', '30%', stats.length, 2, 0, (grid, r, c, i) => {
      const stat = stats[r];
      if (c === 0) {
        const label = this.game.gui2D.createTextBlock('lbl_' + stat.label, stat.label, undefined, '50%');
        label.color = '#AAAAAA';
        label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        label.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        label.width = '90%';
        label.height = '100%';
        return label;
      } else {
        const value = this.game.gui2D.createTextBlock('val_' + stat.label, stat.value, undefined, '50%');
        value.color = stat.color;
        value.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        value.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        value.fontWeight = 'bold';
        value.width = '90%';
        value.height = '100%';
        if (stat.ref) stat.ref(value);
        return value;
      }
    });

    grid.top = '20%';
    grid.left = '0%';
  }

  nextLevel() {
    this.game.sound.play('click');
    this.game.nextLevel(this.levelResult.key);
  }

  selectLevel() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.SELECTION);
  }

  finish() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.FINISH);
  }

  private async createNextLevelButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_next_level',
      '🎮 Next Level',
      () => {
        this.nextLevel();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '35%';
    button.left = '30%';

    return button;
  }

  private async createFinishButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_finish',
      '🎊 Finish',
      () => {
        this.finish();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '35%';
    button.left = '30%';

    return button;
  }

  private async createSelectLevelButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_select_level',
      '🎬 Select Level',
      () => {
        this.selectLevel();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '35%';
    button.left = '-30%';

    return button;
  }
}
